import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import '../../FlowchartView/components/ConfirmModal.css';

/**
 * RenameFieldModal Component
 * 
 * Prompts user to rename a custom field that conflicts with an existing field
 * (same name but different type) during paste operations.
 */
const RenameFieldModal = ({
  isOpen,
  conflictingField, // { name, type, ... } - the field being pasted
  existingFieldType, // The type of the existing field with the same name
  existingFieldNames, // Set or array of all existing field names (lowercase)
  onRename, // (newName) => void
  onCancel, // () => void - skip this field
}) => {
  const [newName, setNewName] = useState('');
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Generate a suggested name when modal opens
  useEffect(() => {
    if (isOpen && conflictingField) {
      const baseName = conflictingField.name;
      let suffix = 1;
      let suggested = `${baseName} (${conflictingField.type})`;
      
      // Keep incrementing suffix until we find a unique name
      const existingSet = existingFieldNames instanceof Set 
        ? existingFieldNames 
        : new Set((existingFieldNames || []).map(n => n.toLowerCase()));
      
      while (existingSet.has(suggested.toLowerCase())) {
        suffix++;
        suggested = `${baseName} (${conflictingField.type} ${suffix})`;
      }
      
      setNewName(suggested);
      setError(null);
    }
  }, [isOpen, conflictingField, existingFieldNames]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isOpen]);

  if (!isOpen || !conflictingField) return null;

  // Determine if this is a reserved name conflict vs type mismatch conflict
  const isReservedNameConflict = existingFieldType === 'built-in';
  
  // Build the conflict message based on conflict type
  const conflictMessage = isReservedNameConflict
    ? <>
        <strong style={{ color: '#fafafa' }}>"{conflictingField.name}"</strong> is a reserved field name used by the system. Custom fields cannot use this name.
      </>
    : <>
        A field named <strong style={{ color: '#fafafa' }}>"{conflictingField.name}"</strong> already exists as a <strong style={{ color: '#fbbf24' }}>{existingFieldType}</strong> field, but the pasted data uses it as a <strong style={{ color: '#fbbf24' }}>{conflictingField.type}</strong> field.
      </>;

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    const trimmedName = newName.trim();
    if (!trimmedName) {
      setError('Please enter a name');
      return;
    }
    
    // Check if name already exists
    const existingSet = existingFieldNames instanceof Set 
      ? existingFieldNames 
      : new Set((existingFieldNames || []).map(n => n.toLowerCase()));
    
    if (existingSet.has(trimmedName.toLowerCase())) {
      setError('This name is already in use. Please choose a different name.');
      return;
    }
    
    onRename(trimmedName);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onCancel?.();
    } else if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel?.();
    }
  };

  const modal = (
    <div 
      className="confirm-modal__backdrop" 
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="confirm-modal__container" data-variant="warning" style={{ width: '420px' }}>
        <div className="confirm-modal__header">
          <div className="confirm-modal__icon-wrapper">
            <div className="confirm-modal__orbit" />
            <div className="confirm-modal__particle" />
            <div className="confirm-modal__particle confirm-modal__particle--secondary" />
            <div className="confirm-modal__icon confirm-modal__icon--warning">
              <span>⚡</span>
            </div>
          </div>
          <h3 className="confirm-modal__title">Field Name Conflict</h3>
        </div>
        
        <div className="confirm-modal__body">
          <p className="confirm-modal__message">
            {conflictMessage}
          </p>
          <p className="confirm-modal__message" style={{ marginTop: '8px' }}>
            Choose a new name for the pasted field:
          </p>
          
          <div style={{ marginTop: '16px' }}>
            <input
              ref={inputRef}
              type="text"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setError(null);
              }}
              onKeyDown={handleKeyDown}
              style={{
                width: '100%',
                padding: '12px 14px',
                fontSize: '14px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: error ? '1px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                color: '#fafafa',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              placeholder="Enter new field name"
            />
            {error && (
              <p style={{
                margin: '8px 0 0',
                fontSize: '12px',
                color: '#f87171',
                textAlign: 'left',
              }}>
                {error}
              </p>
            )}
          </div>
        </div>
        
        <div className="confirm-modal__footer">
          <button 
            className="confirm-modal__btn confirm-modal__btn--cancel"
            onClick={onCancel}
          >
            Skip Field
          </button>
          <button 
            className="confirm-modal__btn confirm-modal__btn--warning"
            onClick={handleSubmit}
          >
            Rename & Add
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default RenameFieldModal;
